import { LoadableSkeleton } from "@/lib/mobx-mui/loadable-skeleton/loadable-skeleton";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import { LatestEpisodeCollection } from "@/modules/episode/LatestEpisodeCollection";
import { ExpandableFeaturedEpisodeList } from "@/modules/featured/ExpandableFeaturedEpisodeList";
import { FeaturedEpisodeCollection } from "@/modules/featured/FeaturedEpisodeCollection";
import { ScreenSection } from "@/modules/look/ScreenSection";
import { Queue } from "@/modules/queue/Queue";
import type { CxProps } from "@/modules/types";
import { ExpandableLatestEpisodesList } from "./ExpandableLatestEpisodesList";
import { ExpandableQueueList } from "./ExpandableQueueList";
import { recordEvent } from "@/lib/nemmp/base/tracking/usage/recordEvent";
import { useEffect } from "react";
import { AppNoticeSection } from "../app-meta/AppNoticeSection";

type DashboardScreenProps = CxProps;

export const DashboardScreen = observer(({ cx }: DashboardScreenProps) => {
  return (
    <Box sx={{ position: "absolute", inset: 0, overflow: "auto", pb: 8 }}>
      <AppNoticeSection cx={cx} />
      <AddPodcastSection cx={cx} />
      <QueueSection cx={cx} />
      <LatestEpisodesSection cx={cx} />
      {/* <FeaturedEpisodesSection cx={cx} /> */}
    </Box>
  );
});

const QueueSection = observer(({ cx }: CxProps) => {
  const { workspace } = cx.wEnv;

  useEffect(() => {
    if (workspace.queue.stateString === "succeeded") {
      recordEvent("loaded-queue", {
        t: Math.round(performance.now() / 1000),
      });
    }
  }, [workspace.queue.stateString]);

  return (
    <LoadableSkeleton
      loadable={workspace.queue}
      renderLoaded={(queue: Queue) => (
        <ScreenSection title="Queue">
          {queue.tracks.length == 0 && (
            <Box sx={{ py: 5, color: "text.secondary" }}>
              Episodes will appear here when you add them to the queue.
            </Box>
          )}
          <ExpandableQueueList queue={queue} cx={cx} />
        </ScreenSection>
      )}
    />
  );
});

const LatestEpisodesSection = observer(({ cx }: CxProps) => {
  const { workspace } = cx.wEnv;

  useEffect(() => {
    if (workspace.latestEpisodeCollection.stateString === "succeeded") {
      recordEvent("loaded-latest-episodes", {
        t: Math.round(performance.now() / 1000),
      });
    }
  }, [workspace.latestEpisodeCollection.stateString]);

  return (
    <LoadableSkeleton
      loadable={workspace.latestEpisodeCollection}
      renderLoaded={(episodeCollection: LatestEpisodeCollection) => (
        <ScreenSection title="Latest Episodes">
          {!episodeCollection.hasAnyItems && (
            <Box sx={{ py: 5, color: "text.secondary" }}>
              Episodes from your subscribed podcasts will appear here.
            </Box>
          )}
          <ExpandableLatestEpisodesList
            cx={cx}
            episodeCollection={episodeCollection}
          />
        </ScreenSection>
      )}
    />
  );
});

const FeaturedEpisodesSection = observer(({ cx }: CxProps) => {
  const { workspace } = cx.wEnv;

  useEffect(() => {
    if (workspace.featuredEpisodeCollection.stateString === "succeeded") {
      recordEvent("loaded-featured-episodes", {
        t: Math.round(performance.now() / 1000),
      });
    }
  }, [workspace.featuredEpisodeCollection.stateString]);

  return (
    <LoadableSkeleton
      loadable={workspace.featuredEpisodeCollection}
      renderLoaded={(featuredEpisodeCollection: FeaturedEpisodeCollection) =>
        featuredEpisodeCollection.hasAnyItems ? (
          <ScreenSection title="Featured Episodes">
            <ExpandableFeaturedEpisodeList
              cx={cx}
              featuredEpisodeCollection={featuredEpisodeCollection}
            />
          </ScreenSection>
        ) : null
      }
    />
  );
});

const AddPodcastSection = observer(({ cx }: CxProps) => {
  const { workspace } = cx.wEnv;
  const subscriptions = workspace.subscriptionCollection.allObs;

  if (subscriptions.length > 0) {
    return null;
  }

  const onClick = async () => {
    recordEvent("click-add-podcast-on-dashboard");
    workspace.podcastSearchState.open();
  };
  return (
    <ScreenSection>
      <Box sx={{ py: 5, color: "text.secondary" }}>
        <Typography sx={{ mb: 2, color: "text.secondary" }}>
          To get started, find a podcast that you would like to listen to.
        </Typography>
        <Button
          onClick={onClick}
          startIcon={<AddIcon />}
          size="large"
          variant="contained"
        >
          Find a podcast
        </Button>
      </Box>
    </ScreenSection>
  );
});
